/* eslint-disable */
import { MEMORY_CONFIG, DEBUG_CONFIG } from '@/constants/memoryConfig'
import { MemoryManager } from '@/services/memoryManager'

export class PerformanceMonitor {
  static isInitialized = false
  static metrics = {
    memory: {
      current: 0,
      peak: 0,
      history: [], // Last 100 readings
      warnings: 0,
      criticalEvents: 0
    },
    performance: {
      frameRate: 0,
      lagSpikes: 0,
      loadTimes: [],
      compressionEvents: 0,
      retirementEvents: 0
    },
    gameplay: {
      totalChocobos: 0,
      totalRaces: 0,
      backgroundRaces: 0,
      daysPlayed: 0,
      saveFileSize: 0
    },
    timestamps: {
      gameStart: 0,
      lastCheck: 0,
      lastWarning: 0,
      lastCritical: 0
    }
  }

  static observers = []
  static warningCallbacks = []

  static initialize() {
    if (this.isInitialized) return

    this.metrics.timestamps.gameStart = Date.now()
    this.isInitialized = true

    // Set up performance monitoring
    this.setupMemoryMonitoring()
    this.setupPerformanceObserver()
    this.setupVisibilityChangeHandler()

  }

  static setupMemoryMonitoring() {
    // Monitor browser memory API if available
    if (performance.memory) {
      this.startMemoryTracking()
    }

    // Set up periodic checks
    setInterval(() => {
      this.collectMetrics()
    }, 30000) // Every 30 seconds
  }

  static setupPerformanceObserver() {
    try {
      // Frame rate monitoring
      if (typeof requestAnimationFrame !== 'undefined') {
        this.startFrameRateMonitoring()
      }

      // Performance entries monitoring
      if (PerformanceObserver) {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            this.processPerformanceEntry(entry)
          }
        })

        observer.observe({ entryTypes: ['measure', 'navigation', 'paint'] })
        this.observers.push(observer)
      }
    } catch (error) {
      console.warn('Performance Observer not supported:', error.message)
    }
  }

  static setupVisibilityChangeHandler() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.onTabVisible()
        } else {
          this.onTabHidden()
        }
      })
    }
  }

  static startMemoryTracking() {
    const trackMemory = () => {
      if (performance.memory) {
        const used = performance.memory.usedJSHeapSize / 1024 / 1024 // MB
        const total = performance.memory.totalJSHeapSize / 1024 / 1024 // MB
        const limit = performance.memory.jsHeapSizeLimit / 1024 / 1024 // MB

        this.updateMemoryMetrics(used, total, limit)
      }

      requestAnimationFrame(trackMemory)
    }

    trackMemory()
  }

  static updateMemoryMetrics(used, total, limit) {
    this.metrics.memory.current = used
    
    if (used > this.metrics.memory.peak) {
      this.metrics.memory.peak = used
    }

    // Add to history (keep last 100 readings)
    this.metrics.memory.history.push({
      timestamp: Date.now(),
      used: used,
      total: total,
      limit: limit
    })

    if (this.metrics.memory.history.length > 100) {
      this.metrics.memory.history.shift()
    }

    // Check for warnings
    this.checkMemoryThresholds(used, limit)
  }

  static checkMemoryThresholds(used, limit) {
    const percentUsed = (used / limit) * 100
    const now = Date.now()

    // Warning threshold
    if (percentUsed > 70 && now - this.metrics.timestamps.lastWarning > 60000) {
      this.metrics.memory.warnings++
      this.metrics.timestamps.lastWarning = now
      this.triggerWarning('memory', {
        current: used,
        percent: percentUsed,
        limit: limit
      })
    }

    // Critical threshold
    if (percentUsed > 85 && now - this.metrics.timestamps.lastCritical > 30000) {
      this.metrics.memory.criticalEvents++
      this.metrics.timestamps.lastCritical = now
      this.triggerCritical('memory', {
        current: used,
        percent: percentUsed,
        limit: limit
      })
    }
  }

  static startFrameRateMonitoring() {
    let lastTime = performance.now()
    let frameCount = 0
    let totalTime = 0

    const measureFrameRate = (currentTime) => {
      frameCount++
      totalTime += currentTime - lastTime
      lastTime = currentTime

      // Calculate FPS every second
      if (totalTime >= 1000) {
        const fps = Math.round((frameCount * 1000) / totalTime)
        this.metrics.performance.frameRate = fps

        // Detect lag spikes (FPS drops below 30)
        if (fps < 30) {
          this.metrics.performance.lagSpikes++
          this.onLagSpike(fps)
        }

        frameCount = 0
        totalTime = 0
      }

      requestAnimationFrame(measureFrameRate)
    }

    requestAnimationFrame(measureFrameRate)
  }

  static processPerformanceEntry(entry) {
    switch (entry.entryType) {
      case 'navigation':
        this.metrics.performance.loadTimes.push({
          type: 'navigation',
          duration: entry.duration,
          timestamp: Date.now()
        })
        break

      case 'measure':
        if (entry.name.includes('compression')) {
          this.metrics.performance.compressionEvents++
        }
        break

      case 'paint':
        // Track paint timing for performance analysis
        break
    }
  }

  static collectMetrics(gameStore = null) {
    this.metrics.timestamps.lastCheck = Date.now()

    if (gameStore) {
      // Update gameplay metrics
      this.metrics.gameplay.totalChocobos = gameStore.chocobos?.length || 0
      this.metrics.gameplay.daysPlayed = gameStore.currentDay || 0
      
      // Estimate total races
      const totalRaces = gameStore.chocobos?.reduce((sum, chocobo) => 
        sum + (chocobo.raceHistory?.length || 0), 0
      ) || 0
      this.metrics.gameplay.totalRaces = totalRaces

      // Get memory report
      const memoryReport = MemoryManager.getMemoryReport(gameStore)
      this.metrics.gameplay.saveFileSize = memoryReport.estimatedUsage
    }

    // Log metrics if in debug mode
    if (DEBUG_CONFIG.showMemoryStats) {
      this.logCurrentMetrics()
    }
  }

  static onLagSpike(fps) {
    console.warn(`Performance: Lag spike detected (${fps} FPS)`)
    
    this.triggerWarning('performance', {
      type: 'lag_spike',
      fps: fps,
      timestamp: Date.now()
    })
  }

  static onTabVisible() {
    // Resume intensive monitoring when tab becomes visible
  }

  static onTabHidden() {
    // Reduce monitoring intensity when tab is hidden
  }

  static triggerWarning(type, data) {
    const warning = {
      type: type,
      level: 'warning',
      data: data,
      timestamp: Date.now()
    }

    console.warn(`Performance Warning (${type}):`, data)

    // Notify registered callbacks
    this.warningCallbacks.forEach(callback => {
      try {
        callback(warning)
      } catch (error) {
        console.error('Warning callback error:', error)
      }
    })
  }

  static triggerCritical(type, data) {
    const critical = {
      type: type,
      level: 'critical',
      data: data,
      timestamp: Date.now()
    }

    console.error(`Performance Critical (${type}):`, data)

    // Notify registered callbacks
    this.warningCallbacks.forEach(callback => {
      try {
        callback(critical)
      } catch (error) {
        console.error('Critical callback error:', error)
      }
    })

    // Auto-trigger emergency cleanup for memory issues
    if (type === 'memory') {
      this.triggerEmergencyOptimization(data)
    }
  }

  static triggerEmergencyOptimization(data) {
    
    // This will be called by the game store when needed
    if (window.gameStore && window.gameStore.emergencyMemoryCleanup) {
      window.gameStore.emergencyMemoryCleanup()
    }
  }

  static registerWarningCallback(callback) {
    this.warningCallbacks.push(callback)
  }

  static unregisterWarningCallback(callback) {
    const index = this.warningCallbacks.indexOf(callback)
    if (index > -1) {
      this.warningCallbacks.splice(index, 1)
    }
  }

  static getMetricsReport() {
    const uptime = Date.now() - this.metrics.timestamps.gameStart
    
    return {
      uptime: uptime,
      uptimeFormatted: this.formatDuration(uptime),
      memory: {
        current: Math.round(this.metrics.memory.current * 100) / 100,
        peak: Math.round(this.metrics.memory.peak * 100) / 100,
        warnings: this.metrics.memory.warnings,
        criticalEvents: this.metrics.memory.criticalEvents,
        history: this.metrics.memory.history.slice(-10) // Last 10 readings
      },
      performance: {
        frameRate: this.metrics.performance.frameRate,
        lagSpikes: this.metrics.performance.lagSpikes,
        compressionEvents: this.metrics.performance.compressionEvents,
        avgLoadTime: this.calculateAverageLoadTime()
      },
      gameplay: {
        ...this.metrics.gameplay,
        racesPerChocobo: this.metrics.gameplay.totalChocobos > 0 
          ? Math.round(this.metrics.gameplay.totalRaces / this.metrics.gameplay.totalChocobos)
          : 0
      },
      recommendations: this.generateRecommendations()
    }
  }

  static calculateAverageLoadTime() {
    if (this.metrics.performance.loadTimes.length === 0) return 0
    
    const total = this.metrics.performance.loadTimes.reduce((sum, time) => sum + time.duration, 0)
    return Math.round(total / this.metrics.performance.loadTimes.length)
  }

  static generateRecommendations() {
    const recommendations = []
    
    if (this.metrics.memory.warnings > 5) {
      recommendations.push('Consider reducing background race frequency or chocobo population')
    }
    
    if (this.metrics.performance.lagSpikes > 10) {
      recommendations.push('Performance issues detected - enable aggressive compression')
    }
    
    if (this.metrics.gameplay.saveFileSize > 5) {
      recommendations.push('Save file is large - consider archiving old data')
    }
    
    if (this.metrics.memory.peak > 200) {
      recommendations.push('High memory usage detected - review optimization settings')
    }

    return recommendations
  }

  static logCurrentMetrics() {
    const report = this.getMetricsReport()
    // Metrics available in report object
  }

  static formatDuration(milliseconds) {
    const seconds = Math.floor(milliseconds / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`
    } else {
      return `${seconds}s`
    }
  }

  static measureOperation(name, operation) {
    const start = performance.now()
    
    try {
      const result = operation()
      const duration = performance.now() - start
      
      if (performance.mark && performance.measure) {
        performance.mark(`${name}-start`)
        performance.mark(`${name}-end`)
        performance.measure(name, `${name}-start`, `${name}-end`)
      }
      
      return result
    } catch (error) {
      const duration = performance.now() - start
      console.error(`Operation ${name} failed after ${duration}ms:`, error)
      throw error
    }
  }

  static startPerformanceProfile(name) {
    if (performance.mark) {
      performance.mark(`${name}-profile-start`)
    }
    return Date.now()
  }

  static endPerformanceProfile(name, startTime) {
    const duration = Date.now() - startTime
    
    if (performance.mark && performance.measure) {
      performance.mark(`${name}-profile-end`)
      performance.measure(`${name}-profile`, `${name}-profile-start`, `${name}-profile-end`)
    }
    
    return duration
  }

  static dispose() {
    // Clean up observers
    this.observers.forEach(observer => {
      try {
        observer.disconnect()
      } catch (error) {
        console.warn('Error disconnecting observer:', error)
      }
    })
    
    this.observers = []
    this.warningCallbacks = []
    this.isInitialized = false
    
  }
}